<!--
	Three states, one panel. The pill and the floating window sit over the page so nothing reflows
	when they open; docking hands the same panel to the app's own side drawer, which is what every
	other right-hand panel in WeQuote uses.
-->
<ng-container *ngIf="available">

	<!--
		The compact form, as the design draws it: a pill that says how much is waiting from
		anywhere in the app. Its + reaches the composer without expanding anything — the design is
		explicit that the + survives the last item being completed, so it sits outside the count.

		Pressing the count opens the list; dragging the pill puts it wherever suits the screen it
		is on, which is the point of it being over the page rather than in the layout.
	-->
	<div *ngIf="mode === 'pill'" #tab class="crm-att crm-att-pill"
			[style.left.px]="tabPos.x" [style.top.px]="tabPos.y"
			(mousedown)="startTabDrag($event)">
		<span class="crm-att-pill-icon"><i class="wq wq-notes"></i></span>

		<!-- One getter rather than three conditions: "All caught up" is only one of the three
		     things a zero can mean, and the pill was saying it for all of them. -->
		<span class="crm-att-pill-label">{{pillLabel}}</span>

		<button class="crm-att-pill-add" (click)="create()" ngbTooltip="Create follow-up"
			aria-label="Create follow-up"><i class="wq wq-add"></i></button>

		<button class="crm-att-pill-open" (click)="setMode('floating')" ngbTooltip="Open"
			aria-label="Open"><i class="wq wq-layout"></i></button>
	</div>

	<!-- The floating window: placed wherever it was left, and moved by its header. -->
	<div *ngIf="mode === 'floating'" class="crm-att crm-att-floating"
			[style.left.px]="pos.x"
			[style.top.px]="pos.y"
			[style.width.px]="size.w"
			[style.height.px]="size.h">

		<ng-container *ngTemplateOutlet="panel"></ng-container>

		<!-- Bottom-right grip, where a window is resized from. -->
		<div class="crm-att-grip" (mousedown)="startResize($event)"
			role="separator" aria-label="Resize window"></div>
	</div>

	<!--
		Docked. The app's own drawer rather than a second one written here: it already handles the
		slide, the backdrop and closing on a click outside, and a panel that behaves like every
		other drawer in WeQuote is one less thing to learn. Closing it returns to the pill, because
		a drawer with a backdrop is something you open and put away, not somewhere you leave a
		window sitting.
	-->
	<ui-sidebar #drawer position="right" [fullscreen]="true" [width]="dockWidth + 'px'"
			(onClose)="onDrawerClosed()">
		<div class="crm-att crm-att-drawer">
			<!-- The drawer grows leftwards from the edge, so its grip is on that edge. -->
			<div class="crm-att-dockgrip" (mousedown)="startResize($event)"
				role="separator" aria-orientation="vertical" aria-label="Resize panel"></div>

			<ng-container *ngTemplateOutlet="panel"></ng-container>
		</div>
	</ui-sidebar>

</ng-container>

<ng-template #panel>
	<div class="crm-att-panel">

		<!-- What this panel is, and the controls that belong to the window rather than the list.
		     The design carries a My Notes tab beside the title; it is not built, so the title is
		     a title rather than a strip of one tab. -->
		<div class="crm-att-head" (mousedown)="startDrag($event)"
				[class.crm-att-draggable]="mode === 'floating'">
			<div class="flex-weight-1 min-width-0">
				<div class="crm-att-name">Needs Your Attention</div>
			</div>

			<button class="btn btn-outline-secondary btn-sm border-0" (click)="load()"
				[disabled]="disabled" ngbTooltip="Refresh"><i class="wq wq-refresh"></i></button>

			<!-- Window controls: dock it to the edge, or shrink it back to the tab. -->
			<button class="btn btn-outline-secondary btn-sm border-0"
					(click)="setMode(mode === 'docked' ? 'floating' : 'docked')"
					[ngbTooltip]="mode === 'docked' ? 'Float this window' : 'Dock to the right'">
				<i class="wq wq-layout"></i>
			</button>
			<button class="btn btn-outline-secondary btn-sm border-0" (click)="setMode('pill')"
				ngbTooltip="Minimise"><i class="wq wq-remove"></i></button>
		</div>

		<!-- Whose work this is, and the one action. Every scope stays offered: what a user may
		     see is already decided server-side, so "All team" for somebody restricted means
		     everything on their own deals — a different list, not an empty one. -->
		<div class="crm-att-scoperow">
			<div class="crm-att-scope flex-weight-1 min-width-0" ngbDropdown [autoClose]="true">
				<button ngbDropdownToggle class="crm-att-scopebtn">
					<b>{{scopeLabel}}</b><i class="wq wq-chevron-down"></i>
				</button>
				<div ngbDropdownMenu class="dropdown-light">
					<button *ngFor="let s of scopes" ngbDropdownItem [class.active]="scope === s.value"
						(click)="setScope(s.value)">{{s.label}}</button>
				</div>
				<div class="crm-att-sub">
					{{countFor('all')}} {{countFor('all') === 1 ? 'item' : 'items'}} &middot; updated {{freshness}}
				</div>
			</div>

			<button class="crm-att-create nowrap" (click)="create()">
				<i class="wq wq-add mr-1"></i>Create follow-up
			</button>
		</div>

		<div class="crm-att-tabs">
			<button *ngFor="let f of filters" [class.active]="filter === f.value" (click)="setFilter(f.value)">
				{{f.label}}<b>{{countFor(f.value)}}</b>
			</button>

			<div class="flex-weight-1"></div>

			<!-- Down to one record. Only records with something waiting are offered, because a
			     list of every deal would be mostly deals with nothing on them. -->
			<div class="crm-att-recordfilter" ngbDropdown placement="bottom-right" [autoClose]="true">
				<div class="crm-att-recordpill" [class.on]="recordFilter">
					<button ngbDropdownToggle class="crm-att-recordbtn">
						<i class="wq wq-filter"></i><span class="text-truncate">{{recordFilterLabel}}</span>
					</button>
					<!-- Clearing it is the thing you want next, and going back into the menu to
					     find "All records" is a step for something that has one answer. -->
					<button *ngIf="recordFilter" class="crm-att-recordclear" (click)="setRecordFilter(null)"
						ngbTooltip="Show all records" placement="left"
						aria-label="Show all records"><i class="wq wq-close"></i></button>
				</div>
				<div ngbDropdownMenu class="dropdown-light crm-att-recordmenu">
					<button ngbDropdownItem [class.active]="!recordFilter" (click)="setRecordFilter(null)">
						All records<b>{{rows.length}}</b>
					</button>
					<div class="dropdown-divider" *ngIf="recordOptions.length"></div>
					<button *ngFor="let r of recordOptions; trackBy: trackRecord" ngbDropdownItem
							[class.active]="recordFilter === r.id" (click)="setRecordFilter(r.id)">
						<span class="text-truncate">{{r.title}}</span><b>{{r.count}}</b>
					</button>
				</div>
			</div>
		</div>

		<div class="crm-att-list">
			<ui-wait *ngIf="!loaded"></ui-wait>

			<!-- The list could not be read. Above whatever is on screen, because when a refresh
			     fails the rows below it are the previous answer and the notice is what says so. -->
			<div *ngIf="loaded && failed"
					class="d-flex align-items-center px-3 py-2 border-bottom text-small">
				<i class="wq wq-warning text-danger mr-2"></i>
				<span class="flex-weight-1 min-width-0">{{failedLabel}}</span>
				<button class="btn btn-link btn-sm p-0 ml-2 nowrap" [disabled]="disabled"
					(click)="load()">Retry</button>
			</div>

			<!-- What stage moves outran. Above the list rather than in it: nobody can tick these
			     off, and putting something unactionable among the actions is how a to-do list
			     stops being one. Dismissed once read — the deal's stage history keeps them. -->
			<div *ngIf="loaded && skipped.length" class="crm-att-skipped">
				<div class="crm-att-skipped-line">
					<button class="crm-att-skipped-main" (click)="skippedOpen = !skippedOpen">
						<i class="wq wq-warning mr-2"></i>
						<span class="flex-weight-1 text-left">
							{{skipped.length}} {{skipped.length === 1 ? 'item was' : 'items were'}}
							skipped when deals moved on
						</span>
						<i class="wq" [ngClass]="skippedOpen ? 'wq-caret-up' : 'wq-caret-down'"></i>
					</button>
					<button class="crm-att-skipped-close" (click)="dismissSkipped()"
						ngbTooltip="Dismiss" placement="left" aria-label="Dismiss"><i class="wq wq-close"></i></button>
				</div>

				<div *ngIf="skippedOpen" class="crm-att-skipped-body">
					<button *ngFor="let s of skipped; trackBy: trackRecord" class="crm-att-skipped-item"
							(click)="goToSkipped(s)">
						<b class="d-block text-truncate">{{s.description}}</b>
						<span class="text-secondary">
							{{s.record_title}} &middot; {{s.stage_name}} &middot;
							outrun {{s.moved_datetime | mySQLDateToISO | localizedDate:'short'}}
						</span>
					</button>
					<div class="text-secondary text-tiny mt-1">
						A quote moved the deal on. Required work holds a person, not a quote, so
						these were never done.
					</div>
				</div>
			</div>

			<ng-container *ngIf="loaded">
				<ng-container *ngFor="let row of visibleRows; trackBy: trackRow">
				<div class="crm-att-row" (click)="goTo(row)">

					<!-- Required work is not a checkbox: it is resolved, and a file one refuses
					     until a file is attached. So it gets its own control and its own word. -->
					<!-- `settled` is the moment between marking something done and the list reloading:
					     the row wears the completed look so it is seen to have been completed,
					     rather than vanishing from under the cursor. -->
					<!-- A question is answered with the Yes and No on its row, so it has no tick. -->
					<span *ngIf="isQuestion(row)" class="crm-att-check holding" [class.done]="row.settled">
						<i class="wq" [ngClass]="row.settled ? 'wq-check' : 'wq-help'"></i>
					</span>
					<button *ngIf="!isQuestion(row)" class="crm-att-check" [class.done]="row.status === 'completed' || row.settled"
							[class.holding]="row.kind === 'required' && !row.settled"
							[disabled]="disabled || row.settled"
							(click)="toggle(row, $event)"
							[ngbTooltip]="row.kind === 'required' ? 'Mark as done'
								: (row.status === 'completed' ? 'Reopen'
									: (timing(row).label === 'Did this happen?' ? 'Yes — it happened'
										: 'Mark complete'))">
						<i class="wq" [ngClass]="row.settled ? 'wq-check'
							: (row.kind === 'required' ? 'wq-lock-closed'
								: (row.status === 'completed' ? 'wq-check' : ''))"></i>
					</button>

					<div class="flex-weight-1 min-width-0">
						<div class="crm-att-title" [class.crm-att-struck]="row.status === 'completed' || row.settled">
							{{row.title}}
						</div>

						<div class="crm-att-meta">
							<span class="crm-att-tag">CRM</span>
							<span>{{sourceLabel(row)}}</span>
							<span class="crm-att-dot">&middot;</span>
							<span [ngClass]="timing(row).className">{{timing(row).label}}</span>
							<ng-container *ngIf="row.due">
								<span class="crm-att-dot">&middot;</span>
								<span>{{row.due | mySQLDateToISO | localizedDate:'short'}}</span>
							</ng-container>
							<span class="crm-att-dot">&middot;</span>
							<span class="crm-att-record">{{row.record_title}}</span>
							<!-- Said as what it is: a bare name read as a label rather than as whose it is. -->
							<ng-container *ngIf="row.assignee_name">
								<span class="crm-att-dot">&middot;</span>
								<span>Assigned to {{row.assignee_id == app.userId ? 'you' : row.assignee_name}}</span>
							</ng-container>
						</div>

						<!-- Who a meeting is with, said as the deal's own card says it: a few by name, the rest counted. -->
						<div *ngIf="row.activity_type === 'meeting' && row.members?.length" class="crm-att-meta">
							<span class="mr-1">Meeting members:</span>
							<span *ngFor="let m of row.members | slice:0:3; let last = last" class="d-inline-flex align-items-center mr-1">
								<ui-user-image class="mr-1" size="1rem" [email]="m.email" [title]="m.name"></ui-user-image>{{m.id == app.userId ? 'You' : m.name}}<ng-container *ngIf="!last">,</ng-container>
							</span>
							<span *ngIf="row.members.length > 3" class="badge badge-light border" [ngbTooltip]="memberNames(row.members, 3)" container="body">+{{row.members.length - 3}}</span>
						</div>
					</div>

					<!-- What you can do without opening the record. Shown on hover, and kept in the
					     DOM while a row's own editor is open so it does not vanish under the mouse.
					     Required work has none of these: it is a requirement, not an activity. -->
					<div *ngIf="canAct(row)" class="crm-att-actions"
						[class.crm-att-actions-open]="replyingTo === row.id || reschedulingId === row.id">
						<!-- The four in a row rather than a list of names: picking a reaction is one
						     glance and one click, and a menu that spells "thumbs up" underneath a
						     thumb is reading the glyph out loud. -->
						<!-- Right-aligned first, so it opens back across the row instead of past the
						     panel's edge — these buttons sit at the right of a 320px widget. The rest
						     are fallbacks, since the widget can be dragged against any edge. -->
						<div ngbDropdown #reactPicker="ngbDropdown" [container]="'body'"
							placement="bottom-right top-right bottom-left top-left" class="d-inline-block">
							<button ngbDropdownToggle class="crm-att-action crm-att-action-plain"
								(click)="$event.stopPropagation()" ngbTooltip="React" container="body">
								<i class="wq wq-feedback"></i>
							</button>
							<div ngbDropdownMenu class="reaction-picker">
								<!-- Closed by hand: these are not ngbDropdownItems, which would bring the
								     list styling the row is deliberately not using. -->
								<button *ngFor="let key of reactionOptions" type="button" class="reaction-picker-option"
									[class.held]="hasReacted(row, key)"
									(click)="react(row, key, $event); reactPicker.close()"
									[ngbTooltip]="key.replace('_', ' ')" container="body">{{glyph(key)}}</button>
							</div>
						</div>
						<button class="crm-att-action" (click)="startReply(row, $event)"
							ngbTooltip="Reply" container="body"><i class="wq wq-notes"></i></button>
						<button class="crm-att-action" (click)="startReschedule(row, $event)"
							ngbTooltip="Reschedule" container="body"><i class="wq wq-clock-update"></i></button>
					</div>

					<div *ngIf="isQuestion(row) && !row.settled" class="d-flex align-items-center nowrap">
						<button class="btn btn-outline-secondary btn-sm mr-1" [disabled]="disabled" (click)="answer(row, true, $event)">Yes</button>
						<button class="btn btn-outline-secondary btn-sm" [disabled]="disabled" (click)="answer(row, false, $event)">No</button>
					</div>

					<i class="wq wq-chevron-right text-secondary"></i>
				</div>

				<!-- The reaction chips and the two editors sit under their row rather than inside
				     it: the row is a fixed line somebody scans, and growing it would move every
				     row below on a hover. -->
				<div *ngIf="canAct(row) && reactionList(row).length" class="crm-att-reactions">
					<button *ngFor="let r of reactionList(row)" class="crm-att-chip"
						[class.held]="hasReacted(row, r.key)" (click)="react(row, r.key, $event)"
						[ngbTooltip]="hasReacted(row, r.key) ? 'Remove your reaction' : 'Add your reaction'"
						container="body">{{glyph(r.key)}} {{r.count}}</button>
				</div>

				<div *ngIf="replyingTo === row.id" class="crm-att-editor" (click)="$event.stopPropagation()">
					<input type="text" class="form-control form-control-sm" [(ngModel)]="replyBody"
						placeholder="Write a quick reply…" [disabled]="disabled"
						(keyup.enter)="saveReply(row)" appDesktopFocus>
					<div class="crm-att-editor-actions">
						<button class="btn btn-medium btn-sm" (click)="cancelReply($event)">Cancel</button>
						<button class="btn btn-primary btn-sm" (click)="saveReply(row, $event)"
							[disabled]="disabled">Send</button>
					</div>
				</div>

				<div *ngIf="reschedulingId === row.id" class="crm-att-editor" (click)="$event.stopPropagation()">
					<label class="crm-att-editor-label">New date and time</label>
					<input type="datetime-local" class="form-control form-control-sm" [(ngModel)]="rescheduleAt"
						[disabled]="disabled" appDesktopFocus>
					<div class="crm-att-editor-actions">
						<button class="btn btn-medium btn-sm" (click)="cancelReschedule($event)">Cancel</button>
						<button class="btn btn-primary btn-sm" (click)="saveReschedule(row, $event)"
							[disabled]="disabled">Save</button>
					</div>
				</div>
				</ng-container>

				<!-- The design's three empty states: they mean different things and say so. None of
				     them is what an unread list means, so a failed load gets the notice above and
				     no empty state at all — the tick and "All caught up" were the false negative. -->
				<div *ngIf="!visibleRows.length && !failed" class="crm-att-empty">
					<i class="wq wq-check mb-2"></i>
					<b class="d-block">
						<ng-container *ngIf="filter === 'open'">All caught up</ng-container>
						<ng-container *ngIf="filter === 'all'">No follow-ups yet</ng-container>
						<ng-container *ngIf="filter === 'completed'">Nothing completed yet</ng-container>
					</b>
					<!-- Said plainly, with the way out next to it: an empty list under a filter is
					     otherwise read as an empty list. -->
					<span *ngIf="recordFilter" class="d-block text-secondary text-small">
						on {{recordFilterLabel}}.
						<button class="btn btn-link btn-sm p-0 align-baseline"
							(click)="setRecordFilter(null)">Show all records</button>
					</span>
					<span class="text-secondary text-small">
						<ng-container *ngIf="filter === 'open'">Nothing is waiting on you right now.</ng-container>
						<ng-container *ngIf="filter === 'all'">Follow-ups you add on a deal appear here.</ng-container>
						<ng-container *ngIf="filter === 'completed'">Items you finish are kept here.</ng-container>
					</span>
				</div>
			</ng-container>
		</div>
	</div>
</ng-template>

<!-- Page-level, outside the widget's own frame: the design is explicit that the composer is not
     inside the widget's scrolling or sizing container, so it stays put whatever the widget does. -->
<app-core-attention-composer #composer (created)="load()"></app-core-attention-composer>
